import React, { useState, useRef, useMemo } from 'react';
import ProductTypeButtons from './ProductTypeButtons';
import Modal from '@components/Modal';
import useIsomorphicLayoutEffect from '@hooks/useIsomorphicLayoutEffect';
import useMatchMedia from '@hooks/useMatchMedia';
import useHasRendered from '@hooks/useHasRendered';
import { SmallSpinner } from '@components/Spinner';
import Percent from './percent.svg';
import * as styles from './ProductTypes.module.scss';
import classNames from 'classnames';
import { isNumber, uniqueId } from 'lodash';
import { isSSR } from '@helpers';

const BASE_DROPDOWN_BREAKPOINT = 755;
const BASE_DROPDOWN_BREAKPOINT_OFFSET = 20;
const BASE_MODAL_CATEGORY_BREAKPOINT = 785;
const MODAL_CATEGORY_BREAKPOINT_OFFSET = 210;

export default React.memo(function ProductTypes({
  className,
  categoryWrapperClassName,
  categoryButtonClassName,
  categoryTabIndex,
  productButtonClassName,
  productTypes = {},
  direction = 'vertical',
  categoryButtonIconSize,
  spinnerSize,
  spinnerThickness,
  isHidden,
  isLoading,
  isLoadingCategory,
  hasErrors,
  dropdownBreakpoint,
  modalCategoryBreakpoint,
  forceDropdown,
  isModal,
  modalIsOpen,
  openModal,
  closeModal,
  dropdownTextBreakpoint,
  showProducts = true,
  toggleProductCategory,
  toggleProductType,
  ...rest
}) {
  //////////////////////////
  // Layout Calculations
  //////////////////////////

  const hasRendered = useHasRendered();
  const sizer = useRef();

  // Breakpoint at which product buttons move to dropdown
  const [dropdownLayout, setDropdownLayout] = useState({
    breakpoint: dropdownBreakpoint ?? BASE_DROPDOWN_BREAKPOINT,
    hasBreakpoint: forceDropdown || isNumber(dropdownBreakpoint)
  });

  useIsomorphicLayoutEffect(() => {
    if (sizer.current) {
      const offset = BASE_DROPDOWN_BREAKPOINT_OFFSET;

      setDropdownLayout({
        breakpoint: sizer.current.offsetWidth + offset,
        hasBreakpoint: true
      });
    }
  }, []);

  dropdownBreakpoint = Math.max(
    dropdownLayout.breakpoint,
    BASE_DROPDOWN_BREAKPOINT
  );

  const isDropdown =
    !!(useMatchMedia(dropdownBreakpoint) || forceDropdown) && !isModal;

  // When displaying product toggles inside a modal,
  // we still display the categories outside the modal
  // until they no longer fit on the screen
  const [modalLayout, setModalLayout] = useState({
    breakpoint: modalCategoryBreakpoint ?? BASE_MODAL_CATEGORY_BREAKPOINT,
    hasBreakpoint: isNumber(modalCategoryBreakpoint)
  });

  modalCategoryBreakpoint = Math.max(
    modalLayout.breakpoint,
    BASE_MODAL_CATEGORY_BREAKPOINT
  );

  const hideModalCategories = useMatchMedia(modalCategoryBreakpoint);

  //////////////////////////
  // Content
  //////////////////////////

  const { fixed, adjustable } = productTypes;
  const modalId = useMemo(() => uniqueId('product-type-modal-'), []);

  const sharedProps = {
    categoryTabIndex,
    isDropdown,
    isModal,
    spinnerSize,
    spinnerThickness,
    categoryButtonIconSize,
    showProducts,
    dropdownTextBreakpoint,
    isHidden,
    hasErrors,
    className: categoryWrapperClassName,
    categoryClassName: categoryButtonClassName,
    productClassName: productButtonClassName,
    onCategoryToggle: toggleProductCategory,
    onProductToggle: toggleProductType,
    'aria-hidden': hasErrors
  };

  const fixedProps = {
    productCategory: 'fixed',
    categoryText: 'Fixed Rate Mortgages',
    categoryLoadingText: 'Loading Fixed Rates',
    products: fixed.products,
    isEnabled: fixed.isEnabled,
    isLoading: isLoading && isLoadingCategory.fixed,
    ...sharedProps
  };

  const adjustableProps = {
    productCategory: 'adjustable',
    categoryText: 'Adjustable Rate Mortgages',
    categoryLoadingText: 'Loading Adjustable Rates',
    products: adjustable.products,
    isEnabled: adjustable.isEnabled,
    isLoading: isLoading && isLoadingCategory.adjustable,
    ...sharedProps
  };

  const modalProps = {
    modalId,
    className,
    isModal,
    isLoading,
    fixedProps,
    adjustableProps,
    openModal,
    categoryTabIndex,
    hideCategories: hideModalCategories,
    hasBreakpoint: modalLayout.hasBreakpoint,
    setLayout: setModalLayout
  };

  const content =
    !fixed && !adjustable ? null : (
      <div
        role="group"
        aria-labelledby="product-types-label"
        className={classNames(styles.wrapper, className, {
          [styles.hidden]: !hasRendered || isHidden,
          [styles.horizontal]: direction === 'horizontal' && !isModal
        })}
        {...rest}
      >
        <ProductTypeButtons {...fixedProps} />
        <ProductTypeButtons {...adjustableProps} />
      </div>
    );

  const modalButtonText = useMatchMedia(
    370,
    'View your rates',
    'View your mortgage rates'
  );

  return (
    <>
      {(isSSR() || dropdownLayout.hasBreakpoint || isModal) && (
        <>
          {!isModal ? (
            content
          ) : (
            <>
              <ModalProductTriggers {...modalProps} />
              <Modal
                id={modalId}
                className={styles.modal}
                isOpen={modalIsOpen}
                header="Select mortgage loan types to compare"
                buttonText={modalButtonText}
                onButtonClick={closeModal}
                close={closeModal}
              >
                {content}
              </Modal>
            </>
          )}
        </>
      )}
      {!isSSR() && !dropdownLayout.hasBreakpoint && !isModal && (
        <div
          ref={sizer}
          className={styles.sizer}
          aria-hidden="true"
        >
          {content}
        </div>
      )}
      {!isSSR() && isModal && !modalLayout.hasBreakpoint}{' '}
      <ModalProductTriggers
        isSizer
        {...modalProps}
      />
    </>
  );
});

const ModalProductTriggers = React.memo(
  ({
    modalId,
    className,
    isModal,
    isLoading,
    hideCategories,
    categoryTabIndex,
    fixedProps,
    adjustableProps,
    isSizer,
    hasBreakpoint,
    openModal,
    setLayout
  }) => {
    const sizer = useRef();

    useIsomorphicLayoutEffect(() => {
      if (sizer.current && !hasBreakpoint) {
        const offset = MODAL_CATEGORY_BREAKPOINT_OFFSET;

        setLayout({
          breakpoint: sizer.current.offsetWidth + offset,
          hasBreakpoint: true
        });
      }
    }, [hasBreakpoint]);

    const content = (
      <div
        role="group"
        aria-label="Toggle product categories"
        className={classNames(styles.modalTriggerWrapper, className, {
          [styles.categoriesVisible]: !hideCategories || isSizer
        })}
      >
        {(!hideCategories || isSizer) && (
          <>
            <ProductTypeButtons
              {...fixedProps}
              isModal={false}
              categoryTabIndex={isSizer ? -1 : categoryTabIndex}
              showProducts={false}
            />
            <ProductTypeButtons
              {...adjustableProps}
              isModal={false}
              categoryTabIndex={isSizer ? -1 : categoryTabIndex}
              showProducts={false}
            />
          </>
        )}
        <button
          aria-controls={modalId}
          tabIndex={isSizer ? -1 : categoryTabIndex}
          className={classNames(
            'product-type-modal-trigger',
            styles.modalTrigger
          )}
          onClick={openModal}
        >
          <span
            className={classNames(styles.icon, {
              [styles.loading]: isLoading
            })}
          >
            <SmallSpinner
              className={styles.spinner}
              thickness={6.6}
            />
            <Percent className={styles.percent} />
          </span>
          Select Products
        </button>
      </div>
    );

    const preventRender =
      isSSR() || (!isModal && !isSizer) || (isSizer && hasBreakpoint);

    return preventRender ? null : (
      <>
        {!isSizer ? (
          content
        ) : (
          <div
            ref={sizer}
            className={styles.sizer}
            aria-hidden="true"
          >
            {content}
          </div>
        )}
      </>
    );
  }
);
